import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Splash } from '../components/Splash'
import { SoundButton } from '../components/SoundButton'
import { Scanning } from '../components/Scanning'
import { Overlay } from '../components/Overlay'
import { MenuView } from '../components/menu/menuView'
import { ScannerTooltipView } from '../components/info/scannerTooltipView'
import { MotionPermissionDialog } from '../components/MotionPermissionDialog'
import { ErrorToast } from '../components/ErrorToast'
import { onTargetView, onTargetLost, endActiveViews, trackDiagnostic } from '../api/Analytics'

// Compensates for print bleed/trim — the trained target image typically includes ~3% of
// design that gets cut off when the postcard is physically printed and trimmed. Without this
// multiplier, the video renders flush to the visible (post-trim) feature region and ends up
// slightly smaller than the actual postcard. Matches the 1.03 multiplier the prior MindAR
// implementation used on the local-space video plane.
const PRINT_BLEED_SCALE = 1.03

// Registered at module load time — AFRAME is synchronously available from the CDN script tag
// in index.html which executes before the React bundle. This component tracks 8th Wall image
// target pose and keeps the A-Frame entity anchored to the physical target on every frame.
if (typeof window.AFRAME !== 'undefined' && !window.AFRAME.components['xr-target-tracker']) {
    window.AFRAME.registerComponent('xr-target-tracker', {
        schema: {
            name: { type: 'string' },
            scanning: { type: 'boolean', default: false },
        },
        init() {
            const targetName = this.data.name
            const scene = this.el.sceneEl

            // Tracks whether the target is currently in frame so that when scanning
            // is enabled mid-detection, the entity can be made visible immediately.
            this._isFound = false

            this._onFound = (e) => {
                if (e.detail.name !== targetName) return
                console.log('[xr-target-tracker] found event detail:', JSON.stringify(e.detail))
                const { position, rotation, scale, scaledWidth, scaledHeight } = e.detail
                this.el.object3D.position.copy(position)
                this.el.object3D.quaternion.copy(rotation)
                if (scale !== undefined) this.el.object3D.scale.setScalar(scale * PRINT_BLEED_SCALE)
                // Use 8th Wall's pre-calculated normalized dimensions so the video
                // plane matches the target's actual visual proportions regardless of
                // what aspect ratio is stored in the project DB.
                const videoEl = this.el.querySelector('a-video')
                if (videoEl && scaledWidth !== undefined && scaledHeight !== undefined) {
                    videoEl.setAttribute('width', scaledWidth)
                    videoEl.setAttribute('height', scaledHeight)
                }
                this._isFound = true
                if (this.data.scanning) this.el.object3D.visible = true
            }
            this._onUpdated = (e) => {
                if (e.detail.name !== targetName) return
                const { position, rotation, scale } = e.detail
                this.el.object3D.position.copy(position)
                this.el.object3D.quaternion.copy(rotation)
                if (scale !== undefined) this.el.object3D.scale.setScalar(scale * PRINT_BLEED_SCALE)
            }
            this._onLost = (e) => {
                if (e.detail.name !== targetName) return
                this._isFound = false
                this.el.object3D.visible = false
            }

            scene.addEventListener('xrimagefound', this._onFound)
            scene.addEventListener('xrimageupdated', this._onUpdated)
            scene.addEventListener('xrimagelost', this._onLost)
        },
        update(oldData) {
            // When scanning is enabled, immediately show the entity if the target
            // is already in frame (handles the "already pointing at target" case).
            if (!oldData.scanning && this.data.scanning && this._isFound) {
                this.el.object3D.visible = true
            }
        },
        remove() {
            const scene = this.el.sceneEl
            scene.removeEventListener('xrimagefound', this._onFound)
            scene.removeEventListener('xrimageupdated', this._onUpdated)
            scene.removeEventListener('xrimagelost', this._onLost)
        },
    })
}

function AFrameViewer({ project, projectId, previewFetchMs, onMissingPermissions }) {
    const sceneRef = useRef(null)
    const loadedOnce = useRef(false)
    // Real network time to download the image-target descriptor (project.mind),
    // captured during setup and reported in the one-shot diagnostic event.
    const assetDownloadMsRef = useRef(0)
    const diagnosticSentRef = useRef(false)

    // ready: 'no'          — splash with project branding; engine/target data still loading
    //        'permissions' — engine loaded but camera permission not yet granted; neutral splash
    //        'yes'         — realityready fired; camera live; show scanning overlay
    const [ready, setReady] = useState('no')

    // Mirrors `ready` so closures inside setTimeout callbacks can read the
    // current value rather than the value captured when scheduled.
    const readyRef = useRef(ready)
    readyRef.current = ready
    const [xrConfigured, setXrConfigured] = useState(false)
    const [engineError, setEngineError] = useState(null)
    const [activeOverlay, setActiveOverlay] = useState(null)
    const [found, setFound] = useState(false)
    const foundRef = useRef(found)
    foundRef.current = found
    const [scanning, setScanning] = useState(false)
    const [video, setVideo] = useState()
    const videoRef = useRef(video)
    videoRef.current = video
    const pausedByBackgroundRef = useRef(false)
    const [src, setSrc] = useState()
    const [index, setIndex] = useState()
    const [button, setButton] = useState()

    // motionPermission gates XR8 setup so the engine never gets a chance to display its own
    // (unbranded) device-motion permission overlay.
    //   'checking'   — initial probe in progress
    //   'needed'     — iOS 13+ and not yet granted; show MotionPermissionDialog
    //   'requesting' — user tapped Continue; iOS native prompt is showing, our dialog is hidden
    //   'granted'    — Android, desktop, older iOS, or already granted on iOS 13+
    //   'denied'     — user cancelled our dialog or iOS rejected the request
    const [motionPermission, setMotionPermission] = useState('checking')

    // Probe for existing device-motion permission on mount. On iOS 13+,
    // DeviceMotionEvent.requestPermission must be invoked from a user gesture, so we can't ask
    // here. Instead we listen briefly for a real `devicemotion` event — if one fires, permission
    // is already granted (Safari emits these continuously when allowed, even when the device is
    // still). If nothing arrives within the probe window, surface the custom dialog so the user's
    // tap on Continue produces the gesture iOS requires.
    useEffect(() => {
        const requiresPrompt =
            typeof DeviceMotionEvent !== 'undefined' &&
            typeof DeviceMotionEvent.requestPermission === 'function'

        if (!requiresPrompt) {
            setMotionPermission('granted')
            return undefined
        }

        let resolved = false
        const onMotion = () => {
            if (resolved) return
            resolved = true
            window.removeEventListener('devicemotion', onMotion)
            setMotionPermission('granted')
        }
        window.addEventListener('devicemotion', onMotion)

        const timeout = setTimeout(() => {
            if (resolved) return
            resolved = true
            window.removeEventListener('devicemotion', onMotion)
            setMotionPermission('needed')
        }, 600)

        return () => {
            clearTimeout(timeout)
            window.removeEventListener('devicemotion', onMotion)
        }
    }, [])

    // Surface the existing missing-permissions screen when the user cancels the dialog or iOS
    // rejects the request. Done as an effect so we don't call the parent setter during render.
    useEffect(() => {
        if (motionPermission === 'denied') onMissingPermissions()
    }, [motionPermission, onMissingPermissions])

    const requestMotionPermission = useCallback(() => {
        // CRITICAL: requestPermission must be called synchronously inside the click handler
        // for iOS to count it as a user gesture. Do not await or wrap before this call.
        try {
            const promise = DeviceMotionEvent.requestPermission()
            // Hide our dialog immediately so it doesn't sit behind the iOS native prompt.
            // setState only queues the React update; the browser still sees the gesture chain
            // intact when the iOS prompt is invoked on the next microtask.
            setMotionPermission('requesting')
            promise
                .then((result) => {
                    setMotionPermission(result === 'granted' ? 'granted' : 'denied')
                })
                .catch(() => setMotionPermission('denied'))
        } catch (_) {
            setMotionPermission('denied')
        }
    }, [])

    const cancelMotionPermission = useCallback(() => {
        setMotionPermission('denied')
    }, [])

    // Fetch the 8th Wall image target descriptor(s) from S3 (project.mind now points to a JSON
    // file produced by image-target-cli instead of a .mind binary) and configure XR8 before the
    // A-Frame scene renders. XR8.XrController.configure must be called before XR8.run(), which
    // is triggered automatically by the xrweb A-Frame component on scene init. Gated on motion
    // permission so xr.js can't render its own permission overlay before ours is dismissed.
    useEffect(() => {
        if (motionPermission !== 'granted') return undefined

        let cancelled = false

        const setup = async () => {
            try {
                const assetStart = performance.now()
                const res = await fetch(project.mind)
                let targetData = await res.json()
                assetDownloadMsRef.current = performance.now() - assetStart
                if (cancelled) return

                // Accept a single descriptor object or an array of descriptors
                if (!Array.isArray(targetData)) targetData = [targetData]

                // Resolve relative imagePath values against the URL the JSON was fetched from.
                // The reprocessing script should write absolute S3 URLs, but this is a safety net.
                targetData = targetData.map((t) => {
                    if (t.imagePath && !/^https?:\/\//.test(t.imagePath)) {
                        const dir = project.mind.substring(
                            0,
                            project.mind.lastIndexOf('/') + 1
                        )
                        return { ...t, imagePath: dir + t.imagePath }
                    }
                    return t
                })

                const configure = () => {
                    if (cancelled) return
                    window.XR8.XrController.configure({ 
                        imageTargetData: targetData,
                    })
                    setXrConfigured(true)
                }

                if (window.XR8) {
                    // Engine already loaded (e.g. sync load or fast async)
                    configure()
                } else {
                    // Engine still loading; show neutral splash while we wait
                    if (!cancelled) setReady('permissions')
                    window.addEventListener('xrloaded', configure, { once: true })
                }
            } catch (err) {
                console.error('[AFrameViewer] Failed to load image target data:', err)
                if (!cancelled) {
                    setEngineError("This experience couldn't be loaded. Please try reloading the page.")
                }
            }
        }

        setup()
        return () => {
            cancelled = true
        }
    }, [project.mind, motionPermission])

    // Fire the one-shot load-timing diagnostic the moment the experience is
    // ready to use (splash dismissed, scanning available). total_load_time_ms is
    // time since page navigation start, so it includes the artificial minimum
    // splash delay in Viewer (and, on iOS, any motion-permission prompt wait).
    useEffect(() => {
        if (ready !== 'yes' || diagnosticSentRef.current) return
        diagnosticSentRef.current = true
        trackDiagnostic(projectId, {
            totalLoadTimeMs: performance.now(),
            previewFetchTimeMs: previewFetchMs,
            assetDownloadTimeMs: assetDownloadMsRef.current,
        })
    }, [ready, projectId, previewFetchMs])

    const playVideo = useCallback(
        (videoSrc, idx) => {
            const vid = document.querySelector(videoSrc)
            vid.play()

            onTargetView(projectId, idx, vid)

            const btn =
                idx !== undefined ? project.image_targets[+idx]?.button : undefined
            setButton(btn)
            setVideo(vid)
            setFound(true)
        },
        [project, projectId]
    )

    const stopVideo = useCallback(
        (videoSrc) => {
            document.querySelector(videoSrc)?.pause()
            setButton(undefined)
            setVideo(undefined)
            setFound(false)
        },
        []
    )

    // Android Chrome keeps document.hidden=false in the app switcher, so blur/focus are the
    // reliable pause/resume signals there. visibilitychange covers iOS and other browsers.
    useEffect(() => {
        const onBackground = () => {
            const vid = videoRef.current
            if (foundRef.current && vid && !vid.paused) {
                pausedByBackgroundRef.current = true
                vid.muted = true
                vid.pause()
            }
        }

        const onForeground = () => {
            if (document.visibilityState !== 'visible' || !document.hasFocus()) return
            if (!(pausedByBackgroundRef.current && foundRef.current && videoRef.current)) return
            // Let the lock/unlock transition fully settle before resuming
            setTimeout(() => {
                if (document.visibilityState === 'visible' && document.hasFocus()
                    && pausedByBackgroundRef.current && foundRef.current && videoRef.current) {
                    videoRef.current.muted = false
                    videoRef.current.play().catch(() => {})
                    pausedByBackgroundRef.current = false
                }
            }, 150)
        }

        window.addEventListener('blur', onBackground)
        window.addEventListener('focus', onForeground)
        const onVisibilityChange = () => {
            if (document.hidden) onBackground()
            else onForeground()
        }
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('blur', onBackground)
            window.removeEventListener('focus', onForeground)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [])

    // Scene-level handlers for active AR (post-scan-button tap)
    const targetFound = useCallback(
        (event) => {
            const { name } = event.detail
            const idx = parseInt(name.split('-').pop(), 10)
            playVideo(`#target-${idx}`, idx)
            console.log('found and playing', name)
        },
        [playVideo]
    )

    const targetLost = useCallback(
        (event) => {
            const { name } = event.detail
            const idx = parseInt(name.split('-').pop(), 10)
            onTargetLost(projectId, idx, document.querySelector(`#target-${idx}`))
            stopVideo(`#target-${idx}`)
            console.log('lost and stopping', name)
        },
        [stopVideo, projectId]
    )

    // Scene-level handlers for pre-scan detection (used to enable immediate play on tap)
    const sourceFound = useCallback((event) => {
        const { name } = event.detail
        const idx = parseInt(name.split('-').pop(), 10)
        setSrc(`#target-${idx}`)
        setIndex(idx)
        console.log('pre-scan found', name)
    }, [])

    const sourceLost = useCallback(() => {
        setSrc(undefined)
        setIndex(undefined)
        console.log('pre-scan lost')
    }, [])

    // Ref callback that attaches 8th Wall event listeners synchronously when the
    // <a-scene> DOM node is first created. This runs before A-Frame's connectedCallback
    // can fire scene init / XR8.run(), guaranteeing we never miss realityready,
    // realityerror, or camerastatuschange — which was causing a race condition on
    // Android where these events fired before useEffect could attach listeners.
    const sceneRefCallback = useCallback((node) => {
        if (!node || loadedOnce.current) return
        loadedOnce.current = true
        sceneRef.current = node

        let realityReadyStuckTimer = null

        node.addEventListener('realityready', () => {
            if (realityReadyStuckTimer) {
                clearTimeout(realityReadyStuckTimer)
                realityReadyStuckTimer = null
            }

            setReady('yes')

            // Suppress native browser video controls and ensure inline playback on iOS
            document.querySelectorAll('video').forEach((v) => {
                v.removeAttribute('controls')
                v.setAttribute('playsinline', '')
                v.setAttribute('webkit-playsinline', 'true')
            })

            // Pre-scan: track whether a target is already in frame so the user gets
            // immediate playback when they tap the scan button
            node.addEventListener('xrimagefound', sourceFound)
            node.addEventListener('xrimagelost', sourceLost)
        })

        // realityerror covers WebGL/WebXR incompatibility and general runtime failures
        node.addEventListener('realityerror', (e) => {
            if (realityReadyStuckTimer) {
                clearTimeout(realityReadyStuckTimer)
                realityReadyStuckTimer = null
            }

            console.error('[AFrameViewer] realityerror:', e)
            setEngineError("The AR experience couldn't start on this device. Please try reloading the page.")
        })

        // camerastatuschange carries camera permission denial on both Android and iOS
        node.addEventListener('camerastatuschange', (e) => {
            const status = e.detail?.status

            if (status === 'failed') {
                if (realityReadyStuckTimer) {
                    clearTimeout(realityReadyStuckTimer)
                    realityReadyStuckTimer = null
                }
                if (readyRef.current === 'yes') {
                    // Tab switch / stream lost after AR was running — reset to scan screen
                    endActiveViews()
                    document.querySelectorAll('video[id^="target-"]').forEach(v => {
                        v.muted = true
                        v.pause()
                    })
                    document.querySelectorAll('video').forEach((v) => {
                        v.removeAttribute('controls')
                        v.setAttribute('playsinline', '')
                        v.setAttribute('webkit-playsinline', 'true')
                    })
                    node.removeEventListener('xrimagefound', targetFound)
                    node.removeEventListener('xrimagelost', targetLost)
                    node.addEventListener('xrimagefound', sourceFound)
                    node.addEventListener('xrimagelost', sourceLost)
                    setButton(undefined)
                    setVideo(undefined)
                    setFound(false)
                    setScanning(false)
                    pausedByBackgroundRef.current = false
                } else {
                    onMissingPermissions()
                }
            }

            if (status === 'declined' || status === 'denied') {
                if (realityReadyStuckTimer) {
                    clearTimeout(realityReadyStuckTimer)
                    realityReadyStuckTimer = null
                }
                onMissingPermissions()
            }

            if (status === 'hasStream' && !realityReadyStuckTimer && readyRef.current !== 'yes') {
                realityReadyStuckTimer = setTimeout(() => {
                    realityReadyStuckTimer = null
                    console.error('[AFrameViewer] realityready timeout — camera stream acquired but engine never initialized')

                    const videos = document.querySelectorAll('video')
                    videos.forEach((v, i) => {
                        console.error(`[AFrameViewer] video[${i}] id="${v.id || '(none)'}" readyState=${v.readyState} networkState=${v.networkState} paused=${v.paused} muted=${v.muted}`)
                    })

                    // 8th Wall's camera feed is the last <video> in the DOM. Known Chromium bug
                    // on Android Chrome: srcObject = MediaStream sometimes leaves the element
                    // stuck at readyState=0. Detaching/reattaching srcObject and calling play()
                    // can unstick it. If it doesn't recover within 2s, surface the error.
                    const camVideo = videos[videos.length - 1]
                    if (camVideo && camVideo.readyState === 0 && camVideo.srcObject) {
                        console.log('[AFrameViewer] attempting to unstick camera video')
                        try {
                            const stream = camVideo.srcObject
                            camVideo.srcObject = null
                            camVideo.srcObject = stream
                            camVideo.play().then(
                                () => console.log('[AFrameViewer] camera video play() resolved after recovery'),
                                (err) => console.warn('[AFrameViewer] camera video play() rejected after recovery:', err)
                            )
                        } catch (err) {
                            console.warn('[AFrameViewer] camera video recovery threw:', err)
                        }

                        // Give the recovery a moment to land. If realityready still hasn't fired,
                        // give up and surface the error.
                        setTimeout(() => {
                            if (readyRef.current !== 'yes') {
                                console.error('[AFrameViewer] recovery did not produce realityready — surfacing error')
                                setEngineError("The AR experience couldn't fully start. Please try reloading the page.")
                            } else {
                                console.log('[AFrameViewer] recovery succeeded — realityready fired')
                            }
                        }, 2000)
                    }
                }, 2000)
            }
        })
    }, [sourceFound, sourceLost, onMissingPermissions, targetFound, targetLost])

    const startScanning = () => {

        setScanning(true)
        console.log('enabling scanning')

        // Unmute all videos for the scan session
        for (const vid of document.getElementsByTagName('video')) {
            vid.muted = false
            vid.removeAttribute('controls')
            vid.setAttribute('playsinline', '')
            vid.setAttribute('webkit-playsinline', 'true')
        }

        const scene = sceneRef.current
        if (scene) {
            // Upgrade from pre-scan (source) listeners to active play listeners
            scene.removeEventListener('xrimagefound', sourceFound)
            scene.removeEventListener('xrimagelost', sourceLost)
            scene.addEventListener('xrimagefound', targetFound)
            scene.addEventListener('xrimagelost', targetLost)

            // Enable visibility in all tracker entities now that scanning has started
            scene.querySelectorAll('[xr-target-tracker]').forEach((el) => {
                el.setAttribute('xr-target-tracker', 'scanning', true)
            })

            if (src !== undefined) {
                console.log('target already in frame — playing immediately')
                playVideo(src, index)
            }
        } else {
            console.log('scene not ready yet')
        }
    }

    // Custom motion-permission dialog. Rendered over the project splash so xr.js never gets a
    // chance to display its own (unbranded) overlay. Tapping Continue triggers the iOS native
    // permission prompt synchronously inside the click handler.
    if (motionPermission === 'needed') {
        return (
            <>
                <Splash id="loading-overlay" variant="scanner" project={project} />
                <MotionPermissionDialog
                    onCancel={cancelMotionPermission}
                    onContinue={requestMotionPermission}
                />
            </>
        )
    }

    // Hold the full render until XR8 has been configured so the a-scene doesn't mount and
    // start XR8.run() before imageTargetData is set.
    if (!xrConfigured) {
        return (
            <>
                <Splash
                    id="loading-overlay"
                    variant="scanner"
                    project={ready === 'no' ? project : undefined}
                />
                <ErrorToast message={engineError} />
            </>
        )
    }

    return (
        <>
            <a-scene
                ref={sceneRefCallback}
                xrweb
                embedded
                vr-mode-ui="enabled: false"
                xr-mode-ui="enabled: false"
                device-orientation-permission-ui="enabled: false"
            >
                <a-assets timeout="2500">
                    {project.image_targets.map((imageTarget, i) => (
                        <video
                            key={`target-${i}`}
                            id={`target-${i}`}
                            src={imageTarget.video2 || imageTarget.video}
                            muted
                            loop
                            crossOrigin="anonymous"
                            preload="auto"
                            playsInline
                            webkit-playsinline="true"
                        />
                    ))}
                </a-assets>

                <a-camera position="0 0 0" look-controls="enabled: false" />

                {project.image_targets.map((imageTarget, i) => (
                    <a-entity
                        key={`entity-${i}`}
                        xr-target-tracker={`name: target-${i}`}
                        visible="false"
                    >
                        <a-video
                            src={`#target-${i}`}
                            data-index={i}
                            width={1}
                            height={(
                                imageTarget.image2
                                    ? imageTarget.height2 / imageTarget.width2
                                    : imageTarget.height / imageTarget.width
                            ).toFixed(3)}
                            position="0 0 0"
                        />
                    </a-entity>
                ))}
            </a-scene>

            <Scanning
                ready={ready}
                hidden={ready !== 'yes' || found}
                scanning={scanning}
                button={button}
                onScanning={startScanning}
            />
            <Overlay
                hidden={ready !== 'yes' || !found}
                projectId={projectId}
                button={button}
                video={video}
            />
            {(ready === 'no' || ready === 'permissions') && (
                <Splash
                    id="loading-overlay"
                    variant="scanner"
                    project={ready === 'no' ? project : undefined}
                />
            )}
            <SoundButton video={video} />
            {ready === 'yes' && (
                <MenuView
                    onOpen={() => setActiveOverlay('menu')}
                    onClose={() => setActiveOverlay(null)}
                    forceClose={activeOverlay === 'tooltip'}
                    disabled={activeOverlay === 'tooltip'}
                />
            )}
            {ready === 'yes' && (
                <ScannerTooltipView
                    onOpen={() => setActiveOverlay('tooltip')}
                    onClose={() => setActiveOverlay(null)}
                    forceClose={activeOverlay === 'menu'}
                    disabled={activeOverlay === 'menu'}
                />
            )}
            <ErrorToast message={engineError} />
        </>
    )
}

export default AFrameViewer
